import type { DeletedUser } from '../../types';
import { coordsToFrequency } from '../../utils/frequency';

interface Props {
  user: DeletedUser;
  onClose: () => void;
}

function val(obj: Record<string, unknown>, key: string): string {
  const v = obj[key];
  if (v == null) return 'N/A';
  if (typeof v === 'object' && v !== null && 'seconds' in v) {
    return new Date((v as { seconds: number }).seconds * 1000).toLocaleString('ko-KR');
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    return (v as { toDate: () => Date }).toDate().toLocaleString('ko-KR');
  }
  return String(v);
}

export default function DeletedUserDetailModal({ user, onClose }: Props) {
  const snap = (user.profileSnapshot ?? {}) as Record<string, unknown>;

  const deletedDate = user.deletedAt && user.deletedAt > 0
    ? new Date(user.deletedAt).toLocaleString('ko-KR') : 'N/A';

  const lat = typeof snap.lat === 'number' ? snap.lat : undefined;
  const lng = typeof snap.lng === 'number' ? snap.lng : undefined;
  const freq = lat != null && lng != null ? coordsToFrequency(lat, lng) : 'N/A';
  const battery = typeof snap.battery === 'number' ? snap.battery : 0;
  const radius = typeof snap.radius === 'number' ? snap.radius : 0;

  const rows: [string, string][] = [
    ['이메일', val(snap, 'email')],
    ['닉네임', val(snap, 'nickname')],
    ['성별', snap.gender === 'male' ? '남성' : snap.gender === 'female' ? '여성' : '미지정'],
    ['관리자', snap.isAdmin ? '예' : '아니오'],
    ['운영자', snap.isOperator ? '예' : '아니오'],
    ['봇', snap.isBot ? '예' : '아니오'],
    ['채팅 상태', snap.chatLocked ? '잠금' : '정상'],
  ];

  const sessionRows: [string, string][] = [
    ['Auth UID', user.uid],
    ['세션 장치', val(snap, 'activeSessionDevice')],
    ['최근 활동', val(snap, 'lastActiveAt')],
    ['생성일', val(snap, 'createdAt')],
    ['최근 업데이트', val(snap, 'updatedAt')],
  ];

  const locationRows: [string, string][] = [
    ['위도', lat?.toFixed(6) ?? 'N/A'],
    ['경도', lng?.toFixed(6) ?? 'N/A'],
    ['주파수', freq],
    ['반경', `${radius.toLocaleString()}m`],
  ];

  const settingsRows: [string, string][] = [
    ['배터리', `${battery.toLocaleString()} 셀`],
    ['푸시 활성', snap.pushNotificationsEnabled ? '예' : '아니오'],
    ['언어', val(snap, 'language')],
    ['자기소개', val(snap, 'introduction')],
  ];

  const profileImg = typeof snap.profileImageUrl === 'string' ? snap.profileImageUrl : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl mx-4 max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-lg">{user.nickname}</h2>
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700">탈퇴</span>
            </div>
            <p className="text-xs text-gray-500">{user.email} | UID: {user.uid}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Deletion info */}
          <Section title="탈퇴 정보" bg="bg-red-50" titleColor="text-red-800" rows={[
            ['탈퇴일', deletedDate],
            ['사유', user.reason || '-'],
            ['문서 ID', user.docId],
            ['제한 상태', user.deletedAt === 0 ? '해제됨' : '제한 중'],
          ]} />

          <Section title="기본 정보 (탈퇴 시점 스냅샷)" rows={rows} />
          <Section title="세션/인증 정보" rows={sessionRows} cols={1} />
          <Section title="위치 정보" rows={locationRows} />
          <Section title="배터리 및 설정" rows={settingsRows} />

          {profileImg && (
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-sm mb-2">프로필 이미지</h3>
              <img src={profileImg} alt="프로필" className="w-20 h-20 rounded-full object-cover" />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg">닫기</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, rows, cols = 2, bg = 'bg-gray-50', titleColor = '' }: {
  title: string;
  rows: [string, string][];
  cols?: number;
  bg?: string;
  titleColor?: string;
}) {
  return (
    <div className={`${bg} rounded-lg p-4`}>
      <h3 className={`font-semibold text-sm mb-2 ${titleColor}`}>{title}</h3>
      <div className={`grid gap-y-1.5 text-sm ${cols === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
        {rows.map(([k, v]) => (
          <div key={k}>
            <span className="text-gray-500">{k}:</span>{' '}
            <span className={`font-medium ${k === 'Auth UID' || k === '문서 ID' ? 'font-mono text-xs break-all' : ''}`}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
